import { type ReactNode, useEffect, useState } from "react";
import { metaApi } from "../api/endpoints/meta.api";
import { useApiQuery } from "../hooks/useApiQuery";
import { DevInspectContext, useDevInspect } from "./devInspect";

// Dev/demo-only ID inspector. The provider resolves `_meta` once, exposes
// `enabled` (env !== "main") to the whole tree so tagged elements stamp their
// ids, and renders the overlay. Never active against the prod backend.
//
//   • Tap Alt (non-prod) → toggles dev mode ON/OFF.
//   • While ON: hover shows the "type #id" + FK tooltip (view only, no click
//     interception) AND tables show the per-task QR button (useDevMode).
//   • The chip (bottom-right) mirrors the state and also toggles on click.

export function DevInspectProvider({ children }: { children: ReactNode }) {
  const { data: meta } = useApiQuery(() => metaApi.get(), []);
  const enabled = !!meta && meta.env !== "main";
  const [on, setOn] = useState(false);
  return (
    <DevInspectContext.Provider value={{ enabled, env: meta?.env ?? null, on, setOn }}>
      {children}
      <DevInspectorOverlay />
    </DevInspectContext.Provider>
  );
}

interface Hovered {
  el: Element;
  id: string;
  type: string;
  label: string | null;
  fields: [string, string | number][];
  rect: DOMRect;
}

function parseFields(raw: string | null, primaryId: string, type: string): [string, string | number][] {
  let entries: [string, string | number][] = [];
  if (raw) {
    try {
      entries = Object.entries(JSON.parse(raw) as Record<string, string | number>);
    } catch {
      entries = [];
    }
  }
  // Show the primary key first, relabeled as "<table>_id" (e.g. order_id) — so the
  // id lives both in the header AND the field list. Prefer the bare "id" field;
  // otherwise the field whose value matches the primary id (e.g. qaId).
  const pkKey = entries.find(([k]) => k === "id")?.[0] ?? entries.find(([, v]) => String(v) === primaryId)?.[0];
  const pkVal = pkKey != null ? entries.find(([k]) => k === pkKey)![1] : primaryId;
  const rest = entries.filter(([k]) => k !== pkKey);
  return [[`${type}_id`, pkVal], ...rest];
}

function DevInspectorOverlay() {
  const { enabled: isDev, env, on, setOn } = useDevInspect();

  const [hovered, setHovered] = useState<Hovered | null>(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const active = isDev && on;

  // Tap Alt to toggle dev mode. We detect a *clean* tap (Alt pressed and
  // released with no other key in between) so Alt+Tab / Alt+click don't flip it.
  useEffect(() => {
    if (!isDev) return;
    let pureAlt = false;
    const down = (e: KeyboardEvent) => {
      if (e.key === "Alt") { if (!e.repeat) pureAlt = true; }
      else pureAlt = false;
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === "Alt" && pureAlt) setOn((m) => !m);
      pureAlt = false;
    };
    const clear = () => { pureAlt = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", clear);
    };
  }, [isDev, setOn]);

  // Hover tracking while active — view only (no click interception, so the
  // per-task QR button and normal row clicks keep working).
  useEffect(() => {
    if (!active) {
      setHovered(null);
      return;
    }
    const move = (e: MouseEvent) => {
      setMouse({ x: e.clientX, y: e.clientY });
      const el = (e.target as Element | null)?.closest?.("[data-devid]") ?? null;
      if (!el) return setHovered(null);
      const id = el.getAttribute("data-devid") ?? "";
      const type = el.getAttribute("data-devtype") ?? "id";
      setHovered({
        el,
        id,
        type,
        label: el.getAttribute("data-devlabel"),
        fields: parseFields(el.getAttribute("data-devfields"), id, type),
        rect: el.getBoundingClientRect(),
      });
    };
    window.addEventListener("mousemove", move, true);
    return () => window.removeEventListener("mousemove", move, true);
  }, [active]);

  if (!isDev) return null;

  const panelH = 52 + (hovered?.fields.length ?? 0) * 18;
  const tipX = Math.min(mouse.x + 14, window.innerWidth - 272);
  const tipY = Math.min(mouse.y + 16, Math.max(12, window.innerHeight - panelH - 12));

  return (
    <>
      {/* hover outline */}
      {active && hovered && (
        <div
          style={{
            position: "fixed",
            left: hovered.rect.left - 2,
            top: hovered.rect.top - 2,
            width: hovered.rect.width + 4,
            height: hovered.rect.height + 4,
            border: "2px solid #E15E2F",
            borderRadius: 6,
            background: "rgba(225,94,47,0.08)",
            pointerEvents: "none",
            zIndex: 2147483646,
          }}
        />
      )}

      {/* cursor tooltip — table + primary id + associated (FK) ids */}
      {active && hovered && (
        <div
          style={{
            position: "fixed",
            left: tipX,
            top: tipY,
            zIndex: 2147483647,
            pointerEvents: "none",
            minWidth: 190,
            maxWidth: 260,
            background: "#0F172A",
            color: "#fff",
            fontFamily: "Menlo, monospace",
            padding: "8px 10px",
            borderRadius: 9,
            boxShadow: "0 6px 18px rgba(0,0,0,.32)",
          }}
        >
          {/* header: table name + primary id */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ color: "#F59E8B", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>{hovered.type}</span>
            <span style={{ fontSize: 13, fontWeight: 800 }}>#{hovered.id}</span>
          </div>
          {hovered.label ? (
            <div style={{ color: "#CBD5E1", fontSize: 11, fontWeight: 500, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{hovered.label}</div>
          ) : null}

          {/* associated ids (foreign keys) */}
          {hovered.fields.length > 0 && (
            <div style={{ marginTop: 7, paddingTop: 6, borderTop: "1px solid #1E293B", display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 10, rowGap: 3 }}>
              {hovered.fields.map(([k, v]) => (
                <div key={k} style={{ display: "contents" }}>
                  <span style={{ color: "#94A3B8", fontSize: 11, fontWeight: 600 }}>{k}</span>
                  <span style={{ color: "#fff", fontSize: 11, fontWeight: 700, textAlign: "right" }}>{v}</span>
                </div>
              ))}
            </div>
          )}

        </div>
      )}

      {/* status chip — Alt toggles dev mode; clicking works too as a fallback */}
      <button
        type="button"
        onClick={() => setOn((m) => !m)}
        title="Modo dev — aperte Alt para ligar/desligar (QR nas tabelas + IDs no hover). Clique aqui também alterna."
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 2147483647,
          display: "flex",
          alignItems: "center",
          gap: 7,
          background: active ? "#E15E2F" : "#0F172A",
          color: "#fff",
          border: "none",
          borderRadius: 999,
          padding: "8px 13px",
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 0.3,
          cursor: "pointer",
          boxShadow: "0 4px 14px rgba(0,0,0,.24)",
          transition: "background .15s",
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 999,
            background: active ? "#fff" : "#22C55E",
            boxShadow: active ? "0 0 0 3px rgba(255,255,255,.3)" : "none",
          }}
        />
        {active ? "DEV on" : "DEV off"}
        <span style={{ opacity: 0.7, fontWeight: 600, fontSize: 10, letterSpacing: 0.6 }}>⌥</span>
        <span style={{ opacity: 0.55, fontWeight: 600, fontSize: 10, letterSpacing: 0.6 }}>{env?.toUpperCase()}</span>
      </button>
    </>
  );
}
